import Dexie, { type EntityTable } from 'dexie';
import type {
  ChecklistItemEntity,
  CompletionEntity,
  ProjectEntity,
  TagEntity,
  TaskEntity,
  TaskTagEntity,
} from '../../../entities/task/model/task.types';
import type { HabitCompletionEntity, HabitEntity } from '../../../entities/habit/model/habit.types';

export interface OutboxEntry {
  client_op_id: string;
  entity_type:
    | 'tag'
    | 'task'
    | 'checklist_item'
    | 'task_tag'
    | 'completion'
    | 'project'
    | 'habit'
    | 'habit_completion';
  entity_id: string;
  updated_at: string;
  deleted: boolean;
  payload: Record<string, unknown> | null;
  created_at: string;
  attempts: number;
  last_error: string | null;
  status: 'pending' | 'inflight';
}

export interface MetaRow {
  key: string;
  value: unknown;
}

export class BrainoteDb extends Dexie {
  tasks!: EntityTable<TaskEntity, 'id'>;
  tags!: EntityTable<TagEntity, 'id'>;
  projects!: EntityTable<ProjectEntity, 'id'>;
  checklist_items!: EntityTable<ChecklistItemEntity, 'id'>;
  task_tags!: EntityTable<TaskTagEntity, 'id'>;
  completions!: EntityTable<CompletionEntity, 'id'>;
  habits!: EntityTable<HabitEntity, 'id'>;
  habit_completions!: EntityTable<HabitCompletionEntity, 'id'>;
  outbox!: EntityTable<OutboxEntry, 'client_op_id'>;
  meta!: EntityTable<MetaRow, 'key'>;

  constructor() {
    super('brainote');

    this.version(1).stores({
      tasks: 'id, user_id, due_date, archived_at, updated_at, dirty, server_revision',
      tags: 'id, user_id, name, updated_at, dirty',
      checklist_items: 'id, task_id, user_id, updated_at, dirty',
      task_tags: 'id, task_id, tag_id, user_id, updated_at',
      completions: 'id, task_id, occurrence_date, user_id, updated_at',
      outbox:
        'client_op_id, entity_type, entity_id, status, created_at, [entity_type+entity_id]',
      meta: 'key',
    });

    // projects + tasks.project_id: upgrade IndexedDB с version(1).
    this.version(2).stores({
      tasks:
        'id, user_id, due_date, archived_at, updated_at, dirty, server_revision, project_id',
      tags: 'id, user_id, name, updated_at, dirty',
      checklist_items: 'id, task_id, user_id, updated_at, dirty',
      task_tags: 'id, task_id, tag_id, user_id, updated_at',
      completions: 'id, task_id, occurrence_date, user_id, updated_at',
      projects: 'id, user_id, name, updated_at, dirty',
      outbox:
        'client_op_id, entity_type, entity_id, status, created_at, [entity_type+entity_id]',
      meta: 'key',
    });

    // habits + habit_completions: upgrade IndexedDB с version(2).
    this.version(3).stores({
      tasks:
        'id, user_id, due_date, archived_at, updated_at, dirty, server_revision, project_id',
      tags: 'id, user_id, name, updated_at, dirty',
      checklist_items: 'id, task_id, user_id, updated_at, dirty',
      task_tags: 'id, task_id, tag_id, user_id, updated_at',
      completions: 'id, task_id, occurrence_date, user_id, updated_at',
      projects: 'id, user_id, name, updated_at, dirty',
      habits: 'id, user_id, name, updated_at, dirty',
      habit_completions: 'id, habit_id, completion_date, user_id, updated_at',
      outbox:
        'client_op_id, entity_type, entity_id, status, created_at, [entity_type+entity_id]',
      meta: 'key',
    });
  }
}

export const db = new BrainoteDb();

export type SyncEntityType = OutboxEntry['entity_type'];

export const ENTITY_TABLES = [
  'tasks',
  'tags',
  'checklist_items',
  'task_tags',
  'completions',
  'projects',
  'habits',
  'habit_completions',
] as const;

export type EntityTableName = (typeof ENTITY_TABLES)[number];
